import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { FormFlowPage, IllustrativeOptions } from "src/components";
import { createStyles } from "src/styles";

import { enums, formatters } from "@fraction/shared";

import { useCheckUnderwriting, useMaybeSubmit } from "../hooks";

const { PROPERTY_USAGE_MAP } = formatters.enums;

export interface PropertyUsageProps {
  onChange: (value: enums.PropertyUsage) => void;
  value?: enums.PropertyUsage;
  propertyAddress?: string;
  isSubpage?: boolean;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const styles = createStyles({
  options: {
    marginBottom: "48px",
  },
});

const PROPERTY_USAGE_OPTIONS = [
  enums.PropertyUsage.PRIMARY_RESIDENCE,
  enums.PropertyUsage.SECONDARY_RESIDENCE,
  enums.PropertyUsage.INVESTMENT,
];

const PropertyUsage = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  propertyAddress,
  isSubpage = false,
  ...props
}: PropertyUsageProps) => {
  const validate = useCallback(() => !!value, [value]);

  const runUnderwriting = useCheckUnderwriting({
    failMessage: "Unfortunately, Fraction only lends on primary residences.",
    getSpecificUnderwritingResult: (result) =>
      result?.propertyProperties?.[enums.UnderwritingRuleName.PROPERTY_USAGE_CHECK],
    onNext,
    onChange,
  });

  const { handleNext, nextDisabled, nextLoading } = useMaybeSubmit({
    onNext: runUnderwriting.mutateAsync,
    validate,
    shouldSubmit: !isSubpage,
  });

  return (
    <FormFlowPage
      {...props}
      title={`How do you use ${propertyAddress || "the property"}?`}
      description="Fraction only works with primary residences."
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={runUnderwriting.nextDisabled || nextDisabled}
      nextLoading={runUnderwriting.isPending || nextLoading}
    >
      <IllustrativeOptions
        label="Property usage"
        placeholder="How's the property used?"
        focusRef={focusRef}
        value={value}
        onChange={runUnderwriting.onChange}
        containerStyle={styles.options}
        options={PROPERTY_USAGE_OPTIONS.map((type) => ({
          value: type,
          label: PROPERTY_USAGE_MAP[type],
        }))}
      />
    </FormFlowPage>
  );
};

export default memo(PropertyUsage);
